/* ============================================================================
   Matriz de riesgo — metodología Eurocopter IN 2255-I-00.

   Una sola matriz para el SGS y para el FRAT. El prototipo ya había unificado
   esto y fue una buena decisión: dos escalas distintas para el mismo vuelo
   producen dos verdades y el piloto termina eligiendo la que le conviene.

   Los umbrales por jurisdicción NO están acá. Llegan en la Fase 5 y se
   inyectan como parámetro. Hoy la app trata a FAA, Perú, Ecuador y Chile con
   idénticos criterios, lo cual es una carencia declarada, no un supuesto.
   ========================================================================== */

export const GRAVEDAD = ["Insignificante", "Menor", "Mayor", "Crítico", "Catastrófico"];
export const PROBABILIDAD = ["Improbable", "Raro", "Reducido", "Probable", "Frecuente"];

/**
 * Filas = gravedad, columnas = probabilidad.
 *
 * Transcrita de la matriz de la página 7 de la IN 2255-I-00. La estructura es
 * diagonal: cada fila se corre una columna respecto de la de arriba.
 *
 * CORRECCIÓN 2026-08: la transcripción anterior tenía 9 de 25 celdas mal, y
 * las 9 erraban hacia el lado permisivo — ninguna hacia el conservador. Las
 * consecuencias no eran cosméticas: piloto con menos de 250 h, viento sobre
 * 25 kt o meteorología sin información daban GRAVE ("se puede volar si el
 * riesgo se atenúa") cuando el manual dice INACEPTABLE ("la actividad no puede
 * proseguir en el estado actual"). Los dos ejemplos resueltos del documento
 * —Mayor x Reducido y Menor x Raro— caen justo en celdas que sí coincidían,
 * que es por lo que el error sobrevivió a la revisión.
 *
 * Cualquier cambio a esta tabla tiene que romper `riesgo.test.js`. Si no lo
 * rompe, el test está mal escrito.
 */
export const MATRIZ_RIESGO = [
  // Insignificante
  ["ACEPTABLE", "ACEPTABLE", "MEDIO", "MEDIO", "GRAVE"],
  // Menor
  ["ACEPTABLE", "MEDIO", "MEDIO", "GRAVE", "INACEPTABLE"],
  // Mayor
  ["MEDIO", "MEDIO", "GRAVE", "INACEPTABLE", "INACEPTABLE+"],
  // Crítico
  ["MEDIO", "GRAVE", "INACEPTABLE", "INACEPTABLE+", "INACEPTABLE+"],
  // Catastrófico
  ["GRAVE", "INACEPTABLE", "INACEPTABLE+", "INACEPTABLE+", "INACEPTABLE+"],
];

/**
 * Criterios de clasificación — leyendas de la página 7 de la IN 2255-I-00.
 *
 * Sin estos criterios a la vista, "Mayor" y "Crítico" son etiquetas vacías y
 * dos personas clasifican el mismo evento distinto. El manual define gravedad
 * por cuatro consecuencias en paralelo, y manda la peor de las cuatro.
 */
export const CRITERIOS_GRAVEDAD = [
  { nivel: "Insignificante", personas: "Heridas superficiales", entorno: "Efectos insignificantes", material: "Daños < 10 000 €", imagen: "Poca repercusión" },
  { nivel: "Menor", personas: "Heridas leves", entorno: "Poca repercusión", material: "Daños < 50 000 €", imagen: "Repercusión limitada" },
  { nivel: "Mayor", personas: "Heridas graves", entorno: "Efectos locales importantes", material: "Daños < 250 000 €", imagen: "Repercusión considerable" },
  { nivel: "Crítico", personas: "Un deceso", entorno: "Efectos de difícil reparación", material: "Daños < 1 000 000 €", imagen: "Repercusión nacional" },
  { nivel: "Catastrófico", personas: "Múltiples decesos", entorno: "Repercusiones masivas (contaminación, destrucciones)", material: "Daños > 1 000 000 €", imagen: "Repercusión internacional" },
];

/**
 * La probabilidad se define contra la historia de ESTA empresa, no contra una
 * tasa teórica. Es la parte que más se malinterpreta: "improbable" no significa
 * "no me parece que pase", significa que nunca pasó en la industria.
 */
export const CRITERIOS_PROBABILIDAD = [
  { nivel: "Improbable", criterio: "Casi inconcebible. Nunca sucedió en la historia de la aviación." },
  { nivel: "Raro", criterio: "Muy poco probable, pero ya sucedió en la historia de la aviación." },
  { nivel: "Reducido", criterio: "Poco probable, pero ya ocurrió al menos una vez en la empresa." },
  { nivel: "Probable", criterio: "Ya se produjo en la empresa, menos de una vez al año." },
  { nivel: "Frecuente", criterio: "Ya se produjo en la empresa, más de una vez al año." },
];

/**
 * Qué significa cada nivel para la decisión de volar (páginas 8 y 11).
 *
 * INACEPTABLE no es "grave, ten cuidado": es que la actividad no puede
 * proseguir en el estado actual.
 */
export const SIGNIFICADO_NIVEL = {
  ACEPTABLE: "El nivel más bajo. No se requiere medida de atenuación.",
  MEDIO: "Riesgo controlable. No se requiere medida de atenuación.",
  GRAVE: "Se puede operar solo si el riesgo se atenúa tanto como sea posible. Requiere medidas de reducción.",
  INACEPTABLE: "La actividad NO puede proseguir en el estado actual. Solo se continúa si el riesgo baja a GRAVE o menos.",
  "INACEPTABLE+": "La actividad NO puede proseguir en el estado actual. Solo se continúa si el riesgo baja a GRAVE o menos.",
};

/** Orden de severidad, para poder comparar y ordenar niveles. */
export const RANGO_NIVEL = {
  ACEPTABLE: 0,
  MEDIO: 1,
  GRAVE: 2,
  INACEPTABLE: 3,
  "INACEPTABLE+": 4,
};

export const COLOR_NIVEL = {
  ACEPTABLE: "#3FAE49",
  MEDIO: "#2E6FBF",
  GRAVE: "#E8C93D",
  INACEPTABLE: "#E8842D",
  "INACEPTABLE+": "#D6392E",
};

/**
 * Nivel de riesgo para un par gravedad/probabilidad.
 *
 * Devuelve null ante índices inválidos en vez de un valor por defecto: el
 * prototipo devolvía "MEDIO" cuando el índice estaba mal, lo que convertía un
 * error de programación en un veredicto de riesgo plausible y silencioso.
 */
export function nivelDeRiesgo(gravedadIdx, probabilidadIdx) {
  const fila = MATRIZ_RIESGO[gravedadIdx];
  if (!fila) return null;
  return fila[probabilidadIdx] ?? null;
}

/**
 * Evalúa un FRAT completo.
 *
 * @param {Array<{key:string, label:string, gravIdx:number, probIdx:number}>} respuestas
 * @returns {object} nivel resultante (el peor de todos), desglose y factores críticos
 */
export function evaluarFrat(respuestas) {
  const items = (respuestas || [])
    .map((r) => {
      const nivel = nivelDeRiesgo(r.gravIdx, r.probIdx);
      return nivel === null ? null : { ...r, nivel, rango: RANGO_NIVEL[nivel] };
    })
    .filter(Boolean);

  if (items.length === 0) {
    return {
      completo: false,
      nivel: null,
      motivo: "sin respuestas válidas",
      items: [],
      criticos: [],
    };
  }

  // El nivel del vuelo es el PEOR factor, no el promedio. Promediar riesgos
  // permite que diez factores buenos tapen uno inaceptable.
  const peor = items.reduce((max, it) => (it.rango > max.rango ? it : max), items[0]);
  const criticos = items.filter((it) => it.rango >= RANGO_NIVEL.GRAVE);

  return {
    completo: true,
    nivel: peor.nivel,
    rango: peor.rango,
    factorDeterminante: peor,
    items,
    criticos,
    requiereMitigacion: peor.rango >= RANGO_NIVEL.GRAVE,
    // Deliberadamente NO se decide "vuela / no vuela": esa decisión es del
    // piloto al mando y de la organización, con su propio procedimiento.
    nota:
      peor.rango >= RANGO_NIVEL.INACEPTABLE
        ? "Nivel inaceptable sin mitigación. Elevar a la instancia que corresponda según el SGS del operador."
        : null,
  };
}

/* --------------------- Matriz coste/beneficio (Figura 3) -------------------- */

export const COSTE = ["Bajo", "Medio", "Elevado"];
export const BENEFICIO = ["Elevado", "Medio", "Bajo"];

export const MATRIZ_COSTE_BENEFICIO = [
  [1, 2, 3],
  [2, 3, 4],
  [3, 4, 5],
];

export function evaluarCosteBeneficio(costeIdx, beneficioIdx) {
  const puntaje = MATRIZ_COSTE_BENEFICIO[costeIdx]?.[beneficioIdx];
  if (puntaje === undefined) return null;
  return {
    puntaje,
    resultado:
      puntaje <= 2
        ? "Se puede adoptar en el estado actual"
        : puntaje === 3
          ? "Si es posible, reducir el coste de la medida"
          : "Volver a analizar el riesgo — buscar otra solución",
  };
}

/* ==========================================================================
   DERIVACIÓN AUTOMÁTICA DESDE LA PLANIFICACIÓN DE VUELO

   Lo que la app puede leer sola, lo lee sola. Un FRAT que se contesta a mano
   entero se contesta rápido y mal: el piloto elige lo de siempre. Los factores
   que salen de un dato objetivo —la cota del terreno en ruta, el margen de
   combustible— se derivan y se muestran bloqueados, con su procedencia.

   Regla de oro: sin dato NO es lo mismo que sin riesgo. Cuando falta la ruta o
   falta el cálculo de combustible, el factor sale en GRAVE (ámbar), nunca en
   ACEPTABLE.
   ========================================================================== */

/** Techo de servicio del AS350 B3, en pies. Sobre esto la aeronave no vuela. */
export const TECHO_SERVICIO_FT = 23000;

/** Umbrales de clasificación topográfica, en pies sobre el nivel del mar. */
export const UMBRAL_MONTANA_FT = 5000;
export const UMBRAL_ALTA_MONTANA_FT = 10000;

/**
 * Índice de la opción de topografía que corresponde a una cota de terreno.
 *
 * Los índices coinciden con las opciones del grupo "topografía" del FRAT:
 *   0 Sin ruta planificada   1 No montañosa   2 Montaña
 *   3 Alta montaña           4 Terreno sobre el techo de servicio
 *
 * @param {number|null} cotaMaximaFt cota máxima del terreno a lo largo de la ruta
 * @param {number} techoFt techo de servicio de la aeronave
 */
export function clasificarTopografia(cotaMaximaFt, techoFt = TECHO_SERVICIO_FT) {
  // Sin dato, dato no numérico, o el -Infinity que produce un reduce sobre un
  // perfil sin ninguna cota leída: los tres significan lo mismo, no sé.
  if (!Number.isFinite(cotaMaximaFt)) {
    return { indice: 0, etiqueta: "Sin ruta planificada", derivado: false, cota: null };
  }
  if (cotaMaximaFt > techoFt) {
    return { indice: 4, etiqueta: `Terreno sobre el techo de servicio (${techoFt} ft)`, derivado: true, cota: cotaMaximaFt };
  }
  if (cotaMaximaFt >= UMBRAL_ALTA_MONTANA_FT) {
    return { indice: 3, etiqueta: "Alta montaña", derivado: true, cota: cotaMaximaFt };
  }
  if (cotaMaximaFt >= UMBRAL_MONTANA_FT) {
    return { indice: 2, etiqueta: "Montaña", derivado: true, cota: cotaMaximaFt };
  }
  return { indice: 1, etiqueta: "No montañosa", derivado: true, cota: cotaMaximaFt };
}

/**
 * Evalúa la reserva de combustible como factor de riesgo.
 *
 * El combustible requerido ya incluye la reserva (por defecto 30 min). El
 * margen es lo que sobra por encima de eso.
 *
 * @param {number|null} margenKg combustible embarcado menos el requerido
 * @param {number} reservaMin minutos de reserva incluidos en el requerido
 */
export function evaluarReserva(margenKg, reservaMin = 30) {
  if (!Number.isFinite(margenKg)) {
    return {
      gravedadIdx: 2, probabilidadIdx: 2, // Mayor x Reducido = GRAVE (ámbar)
      etiqueta: "Sin cálculo de combustible",
      detalle: "No se pudo calcular el combustible requerido: condición fuera de las cartas o sin ruta. No se puede afirmar que la reserva esté cubierta.",
    };
  }
  if (margenKg < 0) {
    return {
      gravedadIdx: 4, probabilidadIdx: 3, // Catastrófico x Probable = INACEPTABLE+
      etiqueta: `Reserva de ${reservaMin} min NO cubierta`,
      detalle: `Faltan ${Math.abs(margenKg).toFixed(0)} kg para completar el vuelo más la reserva de ${reservaMin} min.`,
    };
  }
  if (margenKg < 30) {
    return {
      gravedadIdx: 3, probabilidadIdx: 2, // Crítico x Reducido = INACEPTABLE
      etiqueta: `Reserva de ${reservaMin} min cubierta con margen mínimo`,
      detalle: `Sobran ${margenKg.toFixed(0)} kg sobre la reserva. Cualquier desvío o espera la consume.`,
    };
  }
  return {
    gravedadIdx: 2, probabilidadIdx: 0, // Mayor x Improbable = MEDIO
    etiqueta: `Reserva de ${reservaMin} min cubierta`,
    detalle: `Sobran ${margenKg.toFixed(0)} kg por encima del vuelo más la reserva.`,
  };
}

/**
 * Veredicto de despacho a partir del nivel de riesgo resultante.
 *
 * Traduce los niveles de la página 8 de la IN 2255-I-00 a la única pregunta que
 * importa antes de despegar. GRAVE no es "cuidado": es que se puede volar solo
 * si el riesgo se atenúa y queda constancia de cómo.
 */
export function veredictoDespacho(nivel) {
  if (nivel === "ACEPTABLE" || nivel === "MEDIO") {
    return { permite: true, requiereMitigacion: false, titulo: "VUELO AUTORIZADO",
      detalle: "El nivel de riesgo no requiere medidas de atenuación." };
  }
  if (nivel === "GRAVE") {
    return { permite: true, requiereMitigacion: true, titulo: "AUTORIZADO SOLO CON MITIGACIÓN",
      detalle: "Se puede operar únicamente si el riesgo se atenúa tanto como sea posible, y las medidas quedan registradas." };
  }
  if (nivel === "INACEPTABLE" || nivel === "INACEPTABLE+") {
    return { permite: false, requiereMitigacion: true, titulo: "VUELO NO AUTORIZADO",
      detalle: "La actividad no puede proseguir en el estado actual. Solo se continúa si el riesgo baja a GRAVE o menos." };
  }
  return { permite: false, requiereMitigacion: true, titulo: "SIN VEREDICTO",
    detalle: "No se pudo determinar el nivel de riesgo. Sin evaluación no hay autorización." };
}

/**
 * Límite de viento aprobado para la operación, en nudos.
 *
 * Sobre este valor no se sale a volar. Lo aporta el operador; no es un dato
 * que la aplicación pueda deducir.
 */
export const VIENTO_MAXIMO_KT = 25;

/**
 * Evalúa el viento como factor del FRAT.
 *
 * Los índices coinciden con las opciones del grupo "viento":
 *   0 Sin dato de viento   1 Dentro del límite   2 Sobre el límite
 *
 * Sin dato NO es "dentro del límite". Un vuelo planificado sin saber cuánto
 * viento hay no es un vuelo con viento en calma.
 *
 * @param {number|null} nudos el mayor entre viento sostenido y ráfaga
 */
export function evaluarViento(nudos, limiteKt = VIENTO_MAXIMO_KT) {
  if (!Number.isFinite(nudos)) {
    return {
      indice: 0,
      etiqueta: "Sin dato de viento",
      detalle: "Nadie consultó el viento en despegue ni en aterrizaje. No se puede afirmar que esté dentro del límite.",
      superaLimite: null,
    };
  }
  if (nudos > limiteKt) {
    return {
      indice: 2,
      etiqueta: `${Math.round(nudos)} kt — sobre el máximo aprobado de ${limiteKt} kt`,
      detalle: `El viento (o la ráfaga) supera el límite de ${limiteKt} kt: no se sale a volar.`,
      superaLimite: true,
    };
  }
  return {
    indice: 1,
    etiqueta: `${Math.round(nudos)} kt — dentro del máximo de ${limiteKt} kt`,
    detalle: `Viento dentro del límite aprobado de ${limiteKt} kt.`,
    superaLimite: false,
  };
}

/**
 * Carga de riesgo promedio de un FRAT completo.
 *
 * Promedia el rango numérico de todos los factores y lo devuelve como índice
 * y como nivel equivalente.
 *
 * PARA QUÉ SIRVE Y PARA QUÉ NO
 * ----------------------------
 * Sirve para ver la carga global: si once factores están en MEDIO el promedio
 * lo refleja, cosa que el peor factor solo no muestra.
 *
 * NO sirve para decidir si se vuela. Un promedio diluye: diez factores en
 * MEDIO y uno en INACEPTABLE dan un promedio de MEDIO, y la IN 2255-I-00 dice
 * que INACEPTABLE significa que la actividad no puede proseguir en el estado
 * actual. Ningún promedio cambia eso — igual que un motor en llamas no se
 * compensa con diez sistemas en verde.
 *
 * Por eso el veredicto de despacho sigue saliendo del PEOR factor, y este
 * promedio se muestra al lado como información, no como criterio.
 */
export function promedioDeRiesgo(niveles = []) {
  const validos = niveles.filter((n) => Number.isFinite(RANGO_NIVEL[n]));
  if (validos.length === 0) {
    return { indice: null, promedio: null, nivelEquivalente: null, total: 0, conteo: {} };
  }

  const suma = validos.reduce((s, n) => s + RANGO_NIVEL[n], 0);
  const promedio = suma / validos.length;

  // Se redondea HACIA ARRIBA al nivel equivalente. Un promedio de 1,4 no es
  // MEDIO: hay factores por encima tirando de él, y redondear a la baja los
  // haría desaparecer del resumen.
  const orden = Object.entries(RANGO_NIVEL).sort((a, b) => a[1] - b[1]);
  const indiceRedondeado = Math.min(orden.length - 1, Math.ceil(promedio));
  const nivelEquivalente = orden.find(([, v]) => v === indiceRedondeado)?.[0] ?? null;

  const conteo = {};
  validos.forEach((n) => { conteo[n] = (conteo[n] || 0) + 1; });

  return {
    promedio: Math.round(promedio * 100) / 100,
    // 0 a 100, para leerlo como porcentaje de carga sobre el máximo posible.
    indice: Math.round((promedio / (orden.length - 1)) * 100),
    nivelEquivalente,
    total: validos.length,
    sinEvaluar: niveles.length - validos.length,
    conteo,
  };
}

/* ==========================================================================
   MITIGACIÓN — riesgo inicial y riesgo residual.

   Hasta acá el FRAT daba un solo nivel por factor: el inicial. Eso deja fuera
   la mitad de la metodología. La IN 2255-I-00 distingue dos estrategias
   (sección 8) y las trata distinto:

     · PREVENCIÓN — actúa sobre la PROBABILIDAD de que ocurra el evento.
     · PROTECCIÓN — actúa sobre la GRAVEDAD de las consecuencias si ocurre.

   Y en la sección 8 dice que todo riesgo de GRAVE a INACEPTABLE+ requiere una
   medida que lo baje al menos a MEDIO. O sea: que un factor esté en GRAVE no
   significa que el vuelo no salga; significa que hace falta una medida, y que
   esa medida quede registrada.

   Ejemplo del propio operador: volar alta montaña con un monomotor. Planificar
   una altitud de seguridad sobre el terreno que permita identificar
   visualmente un punto de aterrizaje de emergencia no hace menos grave un
   accidente —sigue siendo un monomotor sobre montaña— pero sí reduce la
   probabilidad de que una falla termine en accidente. Es PREVENCIÓN, y baja la
   probabilidad, no la gravedad.

   Reglas que esto impone:

     1. Una mitigación sin descripción NO reduce nada. Un desplegable que baja
        el riesgo con un clic y sin dejar constancia no es gestión de riesgo,
        es maquillaje.
     2. Cada medida baja UN escalón de su eje. Bajar dos escalones con una
        frase es inventar una eficacia que nadie midió.
     3. La prevención nunca toca la gravedad y la protección nunca toca la
        probabilidad. Confundirlas es lo que hace que un chaleco salvavidas
        parezca reducir la probabilidad de amerizar.
   ========================================================================== */

/**
 * Aplica las medidas declaradas a un factor y devuelve su riesgo residual.
 *
 * @param {{gravedadIdx:number, probabilidadIdx:number}} factor
 * @param {{prevencion?:string, proteccion?:string}} medidas
 */
export function aplicarMitigacion(factor = {}, medidas = {}) {
  const gravedadInicial = Number.isInteger(factor.gravedadIdx) ? factor.gravedadIdx : null;
  const probabilidadInicial = Number.isInteger(factor.probabilidadIdx) ? factor.probabilidadIdx : null;

  if (gravedadInicial === null || probabilidadInicial === null) {
    return {
      valido: false,
      nivelInicial: null, nivelResidual: null,
      gravedadResidual: null, probabilidadResidual: null,
      prevencionAplicada: false, proteccionAplicada: false,
      reduccion: 0,
    };
  }

  const hayTexto = (t) => typeof t === "string" && t.trim().length >= 10;
  const prevencionAplicada = hayTexto(medidas.prevencion);
  const proteccionAplicada = hayTexto(medidas.proteccion);

  const probabilidadResidual = Math.max(0, probabilidadInicial - (prevencionAplicada ? 1 : 0));
  const gravedadResidual = Math.max(0, gravedadInicial - (proteccionAplicada ? 1 : 0));

  const nivelInicial = nivelDeRiesgo(gravedadInicial, probabilidadInicial);
  const nivelResidual = nivelDeRiesgo(gravedadResidual, probabilidadResidual);

  return {
    valido: true,
    nivelInicial,
    nivelResidual,
    gravedadResidual,
    probabilidadResidual,
    prevencionAplicada,
    proteccionAplicada,
    reduccion: (RANGO_NIVEL[nivelInicial] ?? 0) - (RANGO_NIVEL[nivelResidual] ?? 0),
  };
}

/**
 * ¿Este factor exige una medida según la sección 8 de la IN 2255-I-00?
 *
 * De GRAVE para arriba, sí. Y si después de mitigar sigue por encima de MEDIO,
 * la medida declarada no alcanza.
 */
export function exigeMitigacion(nivel) {
  return (RANGO_NIVEL[nivel] ?? 0) >= RANGO_NIVEL.GRAVE;
}
